import { API } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Shield, Search, ChevronDown, User, FileText, Mail,
  Trash2, Edit, RotateCcw, LogIn, Fish, Key, Activity,
  Calendar, Filter, Eye, EyeOff, MapPin, Beaker, Clock, Globe, Download
} from 'lucide-react';
import { toast } from 'sonner';

const NB_M = ['jan','feb','mar','apr','mai','jun','jul','aug','sep','okt','nov','des'];
const NB_DAYS = ['søn','mån','tys','ons','tor','fre','lau'];
const fmtDay = (s) => { const d = new Date(s); if (isNaN(d)) return ''; return `${NB_DAYS[d.getDay()]} ${d.getDate()}. ${NB_M[d.getMonth()]} ${d.getFullYear()}`; };
const fmtTime = (s) => { const d = new Date(s); return isNaN(d) ? '' : `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };

const ACTIONS = {
  login:              { l: 'Pålogging',          I: LogIn,     c: '#3b82f6', bg: '#dbeafe', cat: 'auth' },
  password_change:    { l: 'Passord endra',      I: Key,       c: '#7c3aed', bg: '#ede9fe', cat: 'auth' },
  bulk_create:        { l: 'Prøve oppretta',     I: FileText,  c: '#059669', bg: '#d1fae5', cat: 'data' },
  bulk_edit:          { l: 'Prøve redigert',     I: Edit,      c: '#d97706', bg: '#fef3c7', cat: 'data' },
  bulk_delete:        { l: 'Prøve sletta',       I: Trash2,    c: '#dc2626', bg: '#fee2e2', cat: 'data' },
  draft_create:       { l: 'Utkast oppretta',   I: FileText,  c: '#0ea5e9', bg: '#e0f2fe', cat: 'data' },
  draft_delete:       { l: 'Utkast sletta',      I: Trash2,    c: '#dc2626', bg: '#fee2e2', cat: 'data' },
  draft_restore:      { l: 'Utkast gjenoppretta',I: RotateCcw, c: '#059669', bg: '#d1fae5', cat: 'data' },
  status_change:      { l: 'Status endra',       I: Edit,      c: '#d97706', bg: '#fef3c7', cat: 'data' },
  sent_to_customer:   { l: 'Sendt til kunde',    I: Mail,      c: '#0d9488', bg: '#ccfbf1', cat: 'comm' },
  email_sent:         { l: 'E-post sendt',       I: Mail,      c: '#3b82f6', bg: '#dbeafe', cat: 'comm' },
  pdf_generated:      { l: 'PDF generert',       I: FileText,  c: '#7c3aed', bg: '#ede9fe', cat: 'comm' },
  version_restore:    { l: 'Versjon gjenoppretta',I: RotateCcw,c: '#ea580c', bg: '#ffedd5', cat: 'data' },
  fish_health_create: { l: 'Observasjon',         I: Fish,      c: '#ea580c', bg: '#ffedd5', cat: 'data' },
  user_create:        { l: 'Brukar oppretta',    I: User,      c: '#059669', bg: '#d1fae5', cat: 'admin' },
  user_edit:          { l: 'Brukar redigert',    I: Edit,      c: '#d97706', bg: '#fef3c7', cat: 'admin' },
  user_delete:        { l: 'Brukar sletta',      I: Trash2,    c: '#dc2626', bg: '#fee2e2', cat: 'admin' },
};
const ga = (a) => ACTIONS[a] || { l: a, I: Activity, c: '#64748b', bg: '#f1f5f9', cat: 'other' };

/** Build a human-readable description for an entry */
function buildDescription(entry) {
  const d = entry.details || {};
  const action = entry.action;

  if (action === 'login') {
    const parts = [];
    if (d.user_name) parts.push(d.user_name);
    if (d.role) parts.push(`(${d.role})`);
    if (entry.ip_address) parts.push(`frå ${entry.ip_address}`);
    return parts.join(' ') || null;
  }

  if (action === 'bulk_create' || action === 'bulk_edit') {
    const parts = [];
    if (d.area) parts.push(d.area);
    if (d.date) parts.push(d.date);
    if (d.biologist) parts.push(`Biolog: ${d.biologist}`);
    if (d.sample_count) parts.push(`${d.sample_count} lok.`);
    return parts.join(' · ') || null;
  }

  if (action === 'sent_to_customer') {
    const parts = [];
    if (d.emails?.recipients) parts.push(`${d.emails.sent || 0} e-postar`);
    if (d.sms?.notifications_sent) parts.push(`${d.sms.notifications_sent} SMS`);
    return parts.join(', ') || null;
  }

  if (action === 'email_sent') {
    if (d.recipients) {
      const count = Array.isArray(d.recipients) ? d.recipients.length : d.sent;
      return `${count || 0} mottakar(ar)`;
    }
    return null;
  }

  if (action === 'pdf_generated') {
    const parts = [];
    if (d.area) parts.push(d.area);
    if (d.date) parts.push(d.date);
    if (d.size_kb) parts.push(`${d.size_kb} KB`);
    return parts.join(' · ') || null;
  }

  if (action === 'version_restore') {
    const parts = [];
    if (d.restored_from !== undefined) parts.push(`Frå v${d.restored_from}`);
    if (d.new_version !== undefined) parts.push(`→ v${d.new_version}`);
    return parts.join(' ') || null;
  }

  if (action === 'bulk_delete') {
    return null;
  }

  if (action === 'draft_create' || action === 'draft_delete' || action === 'draft_restore') {
    const parts = [];
    if (d.area) parts.push(d.area);
    if (d.date) parts.push(d.date);
    if (d.biologist) parts.push(`Biolog: ${d.biologist}`);
    return parts.join(' · ') || null;
  }

  if (action === 'status_change') {
    if (d.new_status) return `Ny status: ${d.new_status}`;
    return null;
  }

  return null;
}

export default function AuditLog() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(new Set());
  const [showLogins, setShowLogins] = useState(false);
  const perPage = 50;

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: perPage, skip: page * perPage });

      // Specific action filter
      if (filter !== 'all' && !['auth','data','comm','admin'].includes(filter)) {
        p.append('action', filter);
      }

      // Exclude logins if toggle is off and no specific login filter
      if (!showLogins && filter !== 'login' && filter !== 'auth') {
        p.append('exclude_action', 'login');
      }

      if (search) p.append('user_email', search);

      const res = await axios.get(`${API}/audit/log?${p}`, { withCredentials: true });
      let items = res.data.entries || [];

      // Client-side category filter
      if (['auth','data','comm','admin'].includes(filter)) {
        items = items.filter(e => ga(e.action).cat === filter);
      }
      setEntries(items);
      setTotal(res.data.total || 0);
    } catch { setEntries([]); setTotal(0); }
    finally { setLoading(false); }
  }, [filter, search, page, showLogins]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const toggle = (id) => setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const restoreDraft = async (draftId) => {
    try {
      await axios.post(`${API}/deleted-drafts/${draftId}/restore`, {}, { withCredentials: true });
      toast.success('Utkast gjenoppretta');
      fetchLog();
    } catch { toast.error('Kunne ikkje gjenopprette utkast'); }
  };

  // Stats
  const uniqueUsers = [...new Set(entries.map(e => e.user_email).filter(Boolean))];
  const catCounts = { auth: 0, data: 0, comm: 0, admin: 0 };
  entries.forEach(e => { const c = ga(e.action).cat; if (catCounts[c] !== undefined) catCounts[c]++; });

  // Group by date
  const grouped = {};
  entries.forEach(e => { const d = e.timestamp?.slice(0,10) || '?'; (grouped[d] = grouped[d] || []).push(e); });

  const pageCount = Math.ceil(total / perPage);

  return (
    <div className="flex min-h-screen bg-[#fafbfc]">
      <AdminNavigation />
      <main className="flex-1 min-h-screen p-5 lg:p-8 overflow-x-hidden">
        <div className="max-w-[1100px] mx-auto">
          {/* Header */}
          <div className="mb-7">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-[22px] font-bold text-slate-900 tracking-tight" data-testid="audit-log-title">Endringslogg</h1>
                <p className="text-[13px] text-slate-500 mt-0.5">Sporing av alle system- og dataendringar</p>
              </div>
            </div>
            
            {/* Stats row */}
            <div className="flex items-center gap-3 mt-5">
              <div className="flex items-center gap-6 bg-white border border-slate-200/80 rounded-xl px-5 py-3 flex-1">
                <div>
                  <div className="text-2xl font-bold text-slate-900 tabular-nums">{total}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Totalt</div>
                </div>
                <div className="w-px h-8 bg-slate-100" />
                <div className="flex items-center gap-5">
                  {[
                    { label: 'Autentisering', count: catCounts.auth, icon: LogIn, color: '#3b82f6' },
                    { label: 'Prøvedata', count: catCounts.data, icon: FileText, color: '#d97706' },
                    { label: 'Kommunikasjon', count: catCounts.comm, icon: Mail, color: '#0d9488' },
                    { label: 'Administrasjon', count: catCounts.admin, icon: User, color: '#7c3aed' },
                  ].map(s => (
                    <button key={s.label} onClick={() => { setFilter(filter === s.label.toLowerCase().slice(0,4) ? 'all' : s.label.toLowerCase().slice(0,4)); setPage(0); }}
                      className="text-center group cursor-pointer">
                      <div className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.count}</div>
                      <div className="text-[10px] text-slate-400 group-hover:text-slate-600 transition-colors">{s.label}</div>
                    </button>
                  ))}
                </div>
                <div className="w-px h-8 bg-slate-100" />
                <div>
                  <div className="text-lg font-bold text-slate-700 tabular-nums">{uniqueUsers.length}</div>
                  <div className="text-[10px] text-slate-400">Brukarar</div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters bar */}
          <div className="bg-white border border-slate-200/80 rounded-xl px-4 py-2.5 mb-5 flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400 mr-1" />
            {[
              { id: 'all', label: 'Alle' },
              { id: 'bulk_edit', label: 'Redigert' },
              { id: 'bulk_create', label: 'Oppretta' },
              { id: 'bulk_delete', label: 'Sletta' },
              { id: 'draft_delete', label: 'Utkast sletta' },
              { id: 'sent_to_customer', label: 'Sendt' },
              { id: 'email_sent', label: 'E-post' },
              { id: 'version_restore', label: 'Gjenoppretta' },
              { id: 'user_edit', label: 'Brukar' },
            ].map(f => {
              const isActive = filter === f.id;
              const info = f.id !== 'all' ? ga(f.id) : null;
              return (
                <button key={f.id} onClick={() => { setFilter(isActive ? 'all' : f.id); setPage(0); }}
                  data-testid={`filter-${f.id}`}
                  className={`px-2.5 py-1 rounded-md text-[12px] font-medium transition-all ${
                    isActive ? 'text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                  style={isActive ? { backgroundColor: info?.c || '#0f172a', color: 'white' } : {}}
                >
                  {f.label}
                </button>
              );
            })}

            {/* Login toggle */}
            <div className="h-5 w-px bg-slate-200 mx-1" />
            <button
              onClick={() => { setShowLogins(!showLogins); setPage(0); }}
              data-testid="toggle-logins"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium transition-all ${
                showLogins ? 'bg-blue-100 text-blue-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              {showLogins ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              Pålogging
            </button>

            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 w-3.5 h-3.5" />
                <Input type="text" placeholder="Søk brukar..." value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  data-testid="search-input"
                  className="pl-8 h-8 w-[180px] text-xs border-slate-200 bg-slate-50/50" />
              </div>
            </div>
          </div>

          {/* Timeline */}
          {loading ? (
            <div className="flex items-center justify-center py-28"><div className="w-7 h-7 border-2 border-slate-200 border-t-slate-700 rounded-full animate-spin" /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-28"><Shield className="w-10 h-10 mx-auto mb-3 text-slate-200" /><p className="text-sm text-slate-400">Ingen hendingar funne</p></div>
          ) : (
            Object.entries(grouped).map(([day, dayEntries]) => (
              <div key={day} className="mb-6">
                {/* Date header */}
                <div className="flex items-center gap-3 mb-2.5 px-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-[13px] font-semibold text-slate-700">{fmtDay(day)}</span>
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-[11px] text-slate-400 tabular-nums">{dayEntries.length}</span>
                </div>
                
                {/* Day events */}
                <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden divide-y divide-slate-100/80">
                  {dayEntries.map((entry, i) => {
                    const info = ga(entry.action);
                    const Icon = info.I;
                    const hasChanges = entry.changes?.length > 0;
                    const hasDetails = hasChanges || (entry.details && Object.keys(entry.details).filter(k => !['version','user_name','role'].includes(k)).length > 0);
                    const uid = `${day}-${i}`;
                    const isOpen = expanded.has(uid);
                    const desc = buildDescription(entry);
                    
                    return (
                      <div key={i} className={isOpen ? 'bg-slate-50/40' : ''} data-testid={`audit-entry-${i}`}>
                        <div className={`flex items-center gap-3 px-4 py-3 ${hasDetails ? 'cursor-pointer hover:bg-slate-50/60' : ''} transition-colors`}
                          onClick={() => hasDetails && toggle(uid)}>
                          {/* Icon */}
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: info.bg }}>
                            <Icon className="w-4 h-4" style={{ color: info.c }} />
                          </div>

                          {/* Action + Description */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[13px] font-semibold text-slate-800">{info.l}</span>
                              
                              {/* Version badge */}
                              {entry.details?.version && (
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: info.bg, color: info.c }}>v{entry.details.version}</span>
                              )}
                              
                              {/* Changes count */}
                              {hasChanges && (
                                <span className="text-[10px] font-mono text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                  {entry.changes.length} {entry.changes.length === 1 ? 'endring' : 'endringar'}
                                </span>
                              )}
                            </div>
                            
                            {/* Rich description line */}
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              {/* User - full email */}
                              <span className="text-[12px] text-slate-500 flex items-center gap-1">
                                <User className="w-3 h-3 text-slate-400" />
                                {entry.user_email || '—'}
                              </span>

                              {/* Resource ID */}
                              {entry.resource_id && (
                                <>
                                  <span className="text-slate-300">·</span>
                                  <span className="text-[11px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded truncate max-w-[180px]">{entry.resource_id}</span>
                                </>
                              )}

                              {/* Area / Date from details */}
                              {desc && (
                                <>
                                  <span className="text-slate-300">·</span>
                                  <span className="text-[11px] text-slate-500">{desc}</span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Time + PDF download + Draft restore */}
                          <div className="shrink-0 flex items-center gap-2">
                            {entry.action === 'draft_delete' && entry.resource_id && (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/deleted-draft/${entry.resource_id}`); }}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors border border-indigo-200"
                                  data-testid={`preview-draft-${entry.resource_id}`}
                                >
                                  <Eye className="w-3 h-3" /> Vis
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); restoreDraft(entry.resource_id); }}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                                  data-testid={`restore-draft-${entry.resource_id}`}
                                >
                                  <RotateCcw className="w-3 h-3" /> Gjenopprett
                                </button>
                              </>
                            )}
                            {entry.action === 'pdf_generated' && entry.details?.pdf_id && (
                              <a href={`${API}/reports/pdf-archive/${entry.details.pdf_id}/download`} 
                                 onClick={e => e.stopPropagation()}
                                 className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors"
                                 data-testid={`pdf-download-${entry.details.pdf_id}`}>
                                <Download className="w-3 h-3" /> PDF
                              </a>
                            )}
                            <span className="text-[12px] text-slate-500 tabular-nums font-medium">{fmtTime(entry.timestamp)}</span>
                          </div>
                          {hasDetails && <ChevronDown className={`w-3.5 h-3.5 text-slate-300 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />}
                        </div>
                        
                        {/* Expanded */}
                        {isOpen && (
                          <div className="px-4 pb-3 pt-1">
                            <div className="ml-11 bg-white border border-slate-150 rounded-xl overflow-hidden">
                              {/* Meta bar */}
                              <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-slate-500">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" /> <strong className="text-slate-700">{entry.user_email}</strong></span>
                                {entry.ip_address && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> <span className="font-mono text-slate-600">{entry.ip_address}</span></span>}
                                {entry.resource_id && <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> <span className="font-mono text-slate-600">{entry.resource_id}</span></span>}
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtTime(entry.timestamp)}</span>
                                {entry.details?.area && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {entry.details.area}</span>}
                                {entry.details?.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {entry.details.date}</span>}
                                {entry.details?.biologist && <span className="flex items-center gap-1"><Beaker className="w-3 h-3" /> {entry.details.biologist}</span>}
                              </div>
                              
                              {/* Changes table */}
                              {hasChanges && (
                                <div className="px-4 py-3">
                                  <table className="w-full text-[12px]">
                                    <thead>
                                      <tr className="text-[10px] text-slate-400 uppercase tracking-wider">
                                        <th className="text-left py-1 pr-3 font-medium">Felt</th>
                                        <th className="text-left py-1 pr-3 font-medium">Før</th>
                                        <th className="text-left py-1 font-medium">Etter</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                      {entry.changes.map((c, j) => (
                                        <tr key={j} className="hover:bg-slate-50/50">
                                          <td className="py-1.5 pr-3 font-mono text-slate-500 text-[11px] max-w-[140px] truncate">{c.field}</td>
                                          <td className="py-1.5 pr-3">
                                            <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded text-[11px] line-through max-w-[220px] truncate inline-block">{c.from}</span>
                                          </td>
                                          <td className="py-1.5">
                                            <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[11px] font-medium max-w-[220px] truncate inline-block">{c.to}</span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              
                              {/* Other details */}
                              {entry.details && Object.keys(entry.details).filter(k => !['version','area','date','biologist','sample_count','user_name','role'].includes(k)).length > 0 && (
                                <div className={`px-4 py-3 ${hasChanges ? 'border-t border-slate-100' : ''}`}>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(entry.details).filter(([k]) => !['version','area','date','biologist','sample_count','user_name','role'].includes(k)).map(([k, v]) => (
                                      <div key={k} className="text-[11px] bg-slate-50 border border-slate-100 px-2.5 py-1.5 rounded-lg">
                                        <span className="text-slate-400">{k}: </span>
                                        <span className="text-slate-700 font-medium">{typeof v === 'object' ? JSON.stringify(v).slice(0,80) : String(v).slice(0,80)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          
          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6 mb-4">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                <span className="text-xs">&lsaquo;</span>
              </Button>
              {Array.from({ length: Math.min(pageCount, 5) }, (_, i) => {
                const p = page < 3 ? i : page - 2 + i;
                if (p >= pageCount) return null;
                return (
                  <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                    className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-slate-900' : ''}`}
                    onClick={() => setPage(p)}>{p + 1}</Button>
                );
              })}
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setPage(p => p + 1)} disabled={page >= pageCount - 1}>
                <span className="text-xs">&rsaquo;</span>
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
